import { useEffect } from 'react';
import {
  Drawer,
  FlexColumn,
  LabeledRadioFormField,
} from '@elementary/common-ui/components';
import { AccountEnvironment } from '@elementary/common-ui/types';
import { RadioGroup, Typography } from '@mui/material';
import { Controller, useForm } from 'react-hook-form';
import { EnvScheduleForm } from 'shared/types';
import { fontWeight, space } from '@elementary/common-ui/style/utils';
import WebhookFormSection from './WebhookFormSection';

const updateMethodOptions = [
  {
    value: 'hourly',
    title: 'Hourly updates',
    desc: 'Environment will update on an hourly basis',
  },
  {
    value: 'webhook',
    title: 'Webhook triggered',
    desc: 'Environment will update as soon as new data is available, ensuring you see the most recent data while minimizing unnecessary updates',
  },
];

type Props = {
  open: boolean;
  onSubmit: (data: EnvScheduleForm) => void;
  onClose: () => void;
  data?: AccountEnvironment;
  loading?: boolean;
  error?: string;
};

const EnvScheduleDrawer = ({
  open,
  onClose,
  data,
  loading,
  error,
  onSubmit,
}: Props) => {
  const defaultValues: Partial<EnvScheduleForm> = {
    updateMethod: data?.webhookSetup ? 'webhook' : 'hourly',
  };

  const methods = useForm<EnvScheduleForm>({
    defaultValues,
  });

  useEffect(() => {
    methods.trigger();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [methods.watch('updateMethod')]);

  useEffect(() => {
    if (open) {
      methods.reset(defaultValues);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  return (
    <Drawer
      isOpen={open}
      title="Environment schedule settings"
      onClose={onClose}
      submitButtonLabel={loading ? 'Saving' : 'Save'}
      onSubmit={methods.handleSubmit(onSubmit)}
      submitDisabled={
        !methods.formState.isValid ||
        (methods.watch('updateMethod') === 'webhook' && !methods.watch('token'))
      }
      isLoading={loading}
    >
      <form
        onSubmit={(e) => {
          e.preventDefault();
          e.stopPropagation();
        }}
      >
        <FlexColumn gap={3}>
          <Typography sx={{ fontWeight: fontWeight('medium') }}>
            Select environment update method
          </Typography>
          <Controller
            control={methods.control}
            name="updateMethod"
            render={({ field }) => (
              <RadioGroup sx={{ gap: space('lg') }} {...field}>
                {updateMethodOptions.map((o) => (
                  <LabeledRadioFormField
                    key={o.value}
                    value={o.value}
                    selected={methods.watch('updateMethod') === o.value}
                    label={
                      <FlexColumn>
                        <Typography>{o.title}</Typography>
                        <Typography sx={{ color: 'content.muted' }}>
                          {o.desc}
                        </Typography>
                      </FlexColumn>
                    }
                  />
                ))}
              </RadioGroup>
            )}
          />
          <WebhookFormSection
            environmentId={data?.id}
            methods={methods}
            error={error}
          />
        </FlexColumn>
      </form>
    </Drawer>
  );
};

export default EnvScheduleDrawer;
